/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useMemo} from 'react'
import clsx from 'clsx'
import {PackageCategoryEntity} from 'types/entities/PackageCategoryEntity'
import {PackageCategory} from 'types/enums/PackageCategory'
import {useTranslation} from 'react-i18next'
import BookingAction from 'store/booking/BookingAction'
import {appendQueryParams} from 'utils/utility'
import {useNavigate} from 'react-router-dom'
import {useTypedSelector} from 'helpers/reducers'
import {useAppDispatch} from 'helpers/store'
import {MAX_MONTH_COUNT_FOR_AVAILABLE_DATES} from 'helpers/config'
import {PersonType} from 'types/enums/PersonType'

/**
 * Custom hook `usePackageCategory` for managing and retrieving information
 * about available package categories and their details.
 *
 * This hook is commonly used in contexts where package category data needs
 * to be fetched, processed, or transformed for display or further operation
 * in a component or logic layer, such as listing packages for selection or
 * booking purposes in an amusement park application.
 *
 * @return {Object} Object containing the list of package categories, associated details,
 *                  and utilities for interacting with translations, navigation, and Redux state.
 *
 * - **`sellerStandardCategories`**: List of package categories specifically for standard sellers,
 *   providing detailed information about the packages, such as availability, pricing, tags, images,
 *   and additional features like expiry date and group offers.
 *
 * - **`categories`**: List of general package categories available for booking, with details such as
 *   title, description, availability text, pricing, associated images, and tags. Includes reduced
 *   information compared to `sellerStandardCategories`.
 *
 * @dependencies Uses several external hooks and libraries:
 * - **`useTranslation`**: Provides the `t` utility for translating text/content.
 * - **`useAppDispatch`**: Dispatches Redux actions to the store.
 * - **`useNavigate`**: Simplifies navigation between application routes.
 * - **`useTypedSelector`**: Retrieves typed state from the Redux store.
 * - **`useMemo`**: Optimizes the computation of category data to avoid unnecessary recalculations.
 *
 * @notes
 * - `bookingFilter`: Represents the current filter applied in the booking process,
 *   retrieved from the Redux store.
 * - `validUntil`: Hardcoded validity date for special contexts.
 * - The structure and behavior of categories are designed to support multiple
 *   timeframes (e.g., 2, 4, or 6 hours), group discounts, and additional package details
 *   such as karting options.
 */
export const usePackageCategory = () => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const bookingFilter = useTypedSelector(
        (state) => state.bookingState.bookingProcess.filter
    )
    const validUntil = '17.10.2025'
    const sellerStandardCategories: PackageCategoryEntity[] = useMemo(
        () => [
            {
                id: PackageCategory.General,
                title: t('Standard Tickets'),
                description: (
                    <div>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t(
                                    'The perfect entry-level option to our multi-attraction amusement park, where you get unlimited access to our attractions.'
                                ),
                            }}
                        />

                        {t(
                            'Choose between tickets for 2, 4, or 6 hours, and decide if you want to include 2 adrenaline-filled kart rides on our top-notch electric kart track!'
                        )}
                    </div>
                ),
                timeAvailableText: t('2, 4 or 6 hours'),
                images: [
                    '/assets/images/package_category/standart_1.webp',
                    '/assets/images/package_category/standart_2.webp',
                    '/assets/images/package_category/standart_3.webp',
                ],
                color: 'red-400',
                price: 39,
                shortTitle: t('Possible to reserve with and without karting in the same reservation'),
                tags: [
                    { iconName: 'calendar', title: t('Wednesday till Sunday') },
                    {
                        iconName: 'persons',
                        title: t('personsminimum', { count: 1 }),
                    },
                    {
                        iconName: 'percent',
                        title: t('discount_from_person', { count: 10 }),
                    },
                    { iconName: 'activities', title: t('20+ Attractions') },
                ],
                buttonText: t('Book now'),
                showMoreDetail: true,
                priceImages: [
                    {
                        image: '/assets/images/package_category/details/gelb.webp',
                        alt: '',
                    },
                    {
                        image: '/assets/images/package_category/details/rot.webp',
                        alt: '',
                    },
                ],
                extra: {
                    minDaysFromNow: 0,
                    maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
                    expiryDate: '2026-08-31',
                },
                categoryInfo: {
                    upsell_percent: 0,
                    title: t('Group offer'),
                    subtitle: t(
                        "ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "
                    ),
                    infoText: [
                        t(
                            'Additional kart rides can be purchased for 15 CHF at the kart track for both types of tickets, if capacity is available.'
                        ),
                        t('Balaclavas (for karting) -> CHF 2.00 each (not applicable if you bring your own balaclava)'),
                        t('Please note that cash payments are not possible on site. Attractions cannot be reserved.'),
                    ],
                    infoValues: [
                        {
                            key: t('from number persons', { count: 10 }),
                            value: t('one person free'),
                        },
                        {
                            key: t('from number persons', { count: 20 }),
                            value: t('two persons free'),
                        },
                        {
                            key: t('from number persons', { count: 30 }),
                            value: t('three persons free'),
                        },
                        {
                            key: t('etc...'),
                            value: '',
                        },
                    ],
                },
                enable: true,
                deprecated: false,
                creditBased: true,
            },
        ],
        [t]
    )

    const categories: PackageCategoryEntity[] = useMemo(
        () => [
            {
                id: PackageCategory.General,
                title: t('Standard Tickets'),
                description: (
                    <div>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t(
                                    'The perfect entry-level option to our multi-attraction amusement park, where you get unlimited access to our attractions.'
                                ),
                            }}
                        />
                        <br />
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t(
                                    'Choose between tickets for 2, 4, or 6 hours, and decide if you want to include 2 adrenaline-filled kart rides on our top-notch electric kart track!'
                                ),
                            }}
                        />
                        <br />
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t(
                                    'For a quick thrill after work, there is also the evening ticket from 6 p.m. (one hour).'
                                ),
                            }}
                        />
                        {/*<span*/}
                        {/*    dangerouslySetInnerHTML={{*/}
                        {/*        __html: t("The perfect option for speed junkies who want to take advantage of KartMania."),*/}
                        {/*    }}*/}
                        {/*/>*/}
                    </div>
                ),
                timeAvailableText: t('2, 4 or 6 hours'),

                images: [
                    '/assets/images/package_category/standart_1.webp',
                    '/assets/images/package_category/standart_2.webp',
                    '/assets/images/package_category/standart_3.webp',
                ],
                color: 'red-400',
                price: 39,
                shortTitle: t('Possible to reserve with and without karting in the same reservation'),
                tags: [
                    { iconName: 'calendar', title: t('Wednesday till Sunday') },
                    {
                        iconName: 'persons',
                        title: t('personsminimum', { count: 1 }),
                    },
                    // {iconName: "percent", title: t("discount_from_person", {count: 10})},
                    { iconName: 'activities', title: t('20+ Attractions') },
                ],
                buttonText: t('Book now'),
                showMoreDetail: true,
                priceImages: [
                    {
                        image: '/assets/images/package_category/details/gelb.webp',
                        alt: '',
                    },
                    {
                        image: '/assets/images/package_category/details/rot.webp',
                        alt: '',
                    },
                ],
                extra: {
                    minDaysFromNow: 0,
                    maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
                },
                categoryInfo: {
                    upsell_percent: 0,
                    title: t('Group offer'),
                    subtitle: t(
                        "ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "
                    ),
                    infoText: [
                        t(
                            'Additional kart rides can be purchased for 15 CHF at the kart track for both types of tickets, if capacity is available.'
                        ),
                        t('Balaclavas (for karting) -> CHF 2.00 each (not applicable if you bring your own balaclava)'),
                        t('Please note that cash payments are not possible on site. Attractions cannot be reserved.'),
                    ],
                    infoValues: [
                        // {
                        //     key: t("from number persons", {count: 10}),
                        //     value: t("one person free"),
                        // },
                        // {
                        //     key: t("from number persons", {count: 20}),
                        //     value: t("two persons free"),
                        // },
                        //     {
                        //         key: t("from number persons", {count: 30}),
                        //         value: t("three persons free"),
                        //     },
                        //     {
                        //         key: t("etc..."),
                        //         value: "",
                        //     },
                    ],
                },
                enable: true,
                deprecated: false,
                creditBased: true,
            },
            {
                id: PackageCategory.Action,
                title: t('Family Tickets'),
                description: (
                    <div>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t(
                                    "The perfect choice for unlimited family fun in Switzerland's largest indoor amusement park!"
                                ),
                            }}
                        />
                        <br />
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t(
                                    'Every Wednesday and Sunday, children aged 8 to 15 years receive a 25% discount on the ticket when accompanied by an adult.'
                                ),
                            }}
                        />
                        <br />
                        <span
                            dangerouslySetInnerHTML={{
                                __html: t(
                                    'Choose between tickets for 2, 4 or 6 hours and decide which of you would like to include 2 adrenaline-filled kart rides on our electric kart track!'
                                ),
                            }}
                        />
                    </div>
                ),
                timeAvailableText: t('2, 4 or 6 hours'),

                images: [
                    '/assets/images/package_category/family_1.webp',
                    '/assets/images/package_category/family_2.webp',
                    '/assets/images/package_category/family_3.webp',
                ],
                color: 'purple-400',
                price: 29.25,
                shortTitle: t('Possible to reserve with and without karting in the same reservation'),

                tags: [
                    { iconName: 'calendar', title: t('Wednesday and Sunday') },
                    {
                        iconName: 'persons',
                        title: t('personsmin_max', { min: 2, max: 6 }),
                    },
                    {
                        iconName: 'percent',
                        title: t('discount_for_children', { percent: 25 }),
                    },
                    { iconName: 'activities', title: t('20+ Attractions') },
                ],
                buttonText: t('Book now'),
                showMoreDetail: true,
                priceImages: [
                    {
                        image: '/assets/images/package_category/details/gelb.webp',
                        alt: '',
                    },
                    {
                        image: '/assets/images/package_category/details/rot.webp',
                        alt: '',
                    },
                ],
                extra: {
                    minDaysFromNow: 0,
                    maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
                },
                categoryInfo: {
                    upsell_percent: 0,
                    title: t('Family offer'),
                    subtitle: t('Families can now visit us even cheaper!'),
                    infoText: [
                        t(
                            'Additional kart rides can be purchased for 15 CHF at the kart track for both types of tickets, if capacity is available.'
                        ),
                        t('Balaclavas (for karting) -> CHF 2.00 each (not applicable if you bring your own balaclava)'),
                        t('Please note that cash payments are not possible on site. Attractions cannot be reserved.'),
                    ],
                    infoValues: [
                        {
                            key: t('All children aged 8 to 15 years get 25% discount'),
                            value: '',
                        },
                        {
                            key: t('The Family Ticket is available in two versions: red and yellow'),
                            value: '',
                        },
                        {
                            key: t('With and without karting possible in the same reservation'),
                            value: '',
                        },
                        {
                            key: t('The ticket is valid on Sunday and Wednesday'),
                            value: '',
                        },
                        {
                            key: t('Min. 1 adult (at regular rate), max. 6 people'),
                            value: '',
                        },
                    ],
                },
                enable: true,
                deprecated: false,
                creditBased: true,
            },
            {
                id: PackageCategory.Ferien,
                title: t('Ferien-Spass Offer'),
                description: (
                    <div>
                        <ul>
                            <li
                                dangerouslySetInnerHTML={{
                                    __html: t('Smart holiday offers until', {
                                        date: validUntil,
                                    }),
                                }}
                            ></li>
                            <li
                                dangerouslySetInnerHTML={{
                                    __html: t(
                                        'This _season, parents especially benefit from our flexible holiday packages at ArboPark.',
                                        { season: t('Autumn') }
                                    ),
                                }}
                            ></li>
                            <li
                                dangerouslySetInnerHTML={{
                                    __html: t(
                                        'With % discount for kids until 18 Years, FREE entry for accompanying persons and even active childcare on weekdays.',
                                        { percent: 20 }
                                    ),
                                }}
                            ></li>

                            <br />
                            <li
                                dangerouslySetInnerHTML={{
                                    __html: t(
                                        'Look forward to stress-free days full of action, fun and movement and simply choose the offer that suits your holiday plans!'
                                    ),
                                }}
                            ></li>
                        </ul>
                    </div>
                ),
                timeAvailableText: t('2, 4 or 6 hours'),

                images: [
                    '/assets/images/package_category/Ferien/Ferien-Spass_Angebote_Bild1.jpg',
                    '/assets/images/package_category/Ferien/Ferien-Spass_Angebote_Bild2.jpg',
                    '/assets/images/package_category/Ferien/Ferien-Spass-Angebote_Bild3.jpg',
                ],
                color: 'yellow-400',
                price: 31.2,
                shortTitle: t('Special offer for the _SEASON', {
                    season: t('Autumn'),
                }),
                tags: [
                    { iconName: 'calendar', title: t('Monday till Friday') },
                    {
                        iconName: 'persons',
                        title: t('personsminimum', { count: 1 }),
                    },
                    { iconName: 'activities', title: t('20+ Attractions') },
                ],
                buttonText: t('View more'),
                showMoreDetail: false,
                extra: {
                    minDaysFromNow: 0,
                    maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
                },
                enable: false,
                categoryInfo: null,
                deprecated: false,
                creditBased: true,
            },
            {
                id: PackageCategory.Event,
                title: t('Group packages'),
                description:
                    t(
                        'You’re planning an unforgettable experience for your group? Discover our diverse options, whether it’s a birthday party, bachelor party, or a large corporate event.'
                    ) +
                    t('With our unique packages, you’ll find exactly what you’re looking for!') +
                    '\r\n' +
                    '\r\n' +
                    t(
                        'Enjoy maximum comfort and unique experiences: from a reserved area in the restaurant section to the thrilling Competition mode, exclusive stint guarantee, and personalized VIP care – every visit becomes something special here.'
                    ),
                timeAvailableText: t('2, 3, 4 or 6 hours'),
                images: [
                    '/assets/images/package_category/events_1.webp',
                    '/assets/images/package_category/events_2.webp',
                    '/assets/images/package_category/events_3.webp',
                ],
                color: 'yellow-400',
                price: 29.25,
                shortTitle: t('Whether it’s a small or large group event, we’ve got you covered!'),
                tags: [
                    { iconName: 'calendar', title: t('Wednesday till Sunday') },
                    {
                        iconName: 'persons',
                        title: t('personsminimum', { count: 5 }),
                    },
                    { iconName: 'activities', title: t('20+ Attractions') },
                ],
                buttonText: t('View more'),
                showMoreDetail: false,
                extra: {
                    minDaysFromNow: 0,
                    maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
                },
                enable: true,
                categoryInfo: null,
                deprecated: false,
                creditBased: true,
            },
            {
                id: PackageCategory.Giftcard,
                title: t('Gift voucher'),
                description: t(
                    'Give the gift of pure pleasure with the ArboPark gift voucher, entry to exciting experiences in the largest indoor leisure park in Switzerland.'
                ),
                timeAvailableText: t('2, 4 or 6 hours'),

                images: [
                    '/assets/images/package_category/gutschein.webp',
                    '/assets/images/package_category/gutschein_2.webp',
                ],
                color: 'green-400',
                price: 40,
                shortTitle: t('Your ticket to unforgettable activities'),
                tags: [],
                buttonText: t('Buy now'),
                showMoreDetail: false,
                enable: true,
                categoryInfo: null,
                deprecated: false,
                creditBased: true,
            },
            {
                id: PackageCategory.Yearly,
                title: t("Arbo- Abo's"),
                description: (
                    <div>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: t('With our subscriptions'),
                            }}
                        />
                    </div>
                ),
                timeAvailableText: t('2, 4 or 6 hours'),
                images: ['/assets/images/package_category/Jahr.webp', '/assets/images/package_category/Halb.webp'],
                color: 'green-400',
                price: 295,
                shortTitle: t('Constant fun for less money'),
                tags: [
                    { iconName: 'calendar', title: t('Wednesday till Sunday') },
                    {
                        iconName: 'persons',
                        title: t('personsminimum', { count: 1 }),
                    },
                    { iconName: 'activities', title: t('20+ Attractions') },
                ],
                buttonText: t('Buy now'),
                showMoreDetail: false,
                enable: false,
                categoryInfo: null,
                deprecated: true,
                creditBased: true,
            },
        ],
        [t]
    )

    const ferienSubcategories: PackageCategoryEntity[] = useMemo(
        () => [
            {
                id: PackageCategory.FerienPass,
                title: t('Buddy Ferien-Spass'),
                description: (
                    <div>
                        <h3>
                            <b>{t("The perfect holiday fun for large children's groups")}</b>
                        </h3>
                        <ul className={clsx('list-disc pl-5')}>
                            <li>
                                <b>{t('Bookable from 5 people')}</b>
                            </li>
                            <li>
                                <b>{t('2, 4 or 6 hours tickets')}</b>, {t('with or without karting')}
                            </li>
                            <li>
                                {t('One teamleader receives')} <b>{t('a free entry')}</b>
                            </li>
                            <li>
                                {t('Children under 18 years of age receive')}
                                &nbsp;
                                <b>
                                    {t('% discount on the ticket price', {
                                        percent: 20,
                                    })}
                                </b>
                            </li>
                            <li
                                dangerouslySetInnerHTML={{
                                    __html: t('Available until {validUntil}', {
                                        validUntil: validUntil,
                                    }),
                                }}
                            ></li>
                        </ul>
                    </div>
                ),
                timeAvailableText: t('2, 4 or 6 hours'),
                images: [
                    '/assets/images/package_category/Ferien/Buddy/Buddy_Bild1.jpg',
                    '/assets/images/package_category/Ferien/Buddy/Buddy_Bild2.jpg',
                    '/assets/images/package_category/Ferien/Buddy/Buddy_Bild3.jpg',
                ],
                color: 'red-400',
                price: 31.2,
                shortTitle: t('Special offer for the _SEASON', {
                    season: t('Autumn'),
                }),
                tags: [
                    { iconName: 'calendar', title: t('Monday till Friday') },
                    {
                        iconName: 'persons',
                        title: t('personsminimum', { count: 5 }),
                    },
                    { iconName: 'percent', title: t('Team leader gratis') },
                    { iconName: 'activities', title: t('20+ Attractions') },
                ],
                buttonText: t('Book now'),
                showMoreDetail: true,
                priceImages: [
                    {
                        image: '/assets/images/package_category/details/gelb.webp',
                        alt: '',
                    },
                    {
                        image: '/assets/images/package_category/details/rot.webp',
                        alt: '',
                    },
                ],
                extra: {
                    minDaysFromNow: 0,
                    maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
                    releaseDate: new Date('2025-09-29'),
                    expiryDate: new Date('2026-08-31'),
                    maximumDate: new Date('2025-10-17'),
                },
                categoryInfo: {
                    upsell_percent: 0,
                    title: t('Group offer'),
                    subtitle: t(
                        "ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "
                    ),
                    infoText: [
                        t(
                            'Additional kart rides can be purchased for 15 CHF at the kart track for both types of tickets, if capacity is available.'
                        ),
                        t('Balaclavas (for karting) -> CHF 2.00 each (not applicable if you bring your own balaclava)'),
                        t('Please note that cash payments are not possible on site. Attractions cannot be reserved.'),
                    ],
                    infoValues: [
                        {
                            key: t('All children aged 8 to 17 years get % discount', { percent: 20 }),
                            value: '',
                        },
                        {
                            key: t('The Ferienpass is available in two versions: red and yellow'),
                            value: '',
                        },
                        {
                            key: t('With and without karting possible in the same reservation'),
                            value: '',
                        },
                        {
                            key: t('The ticket is valid Monday till Friday'),
                            value: '',
                        },
                        {
                            key: t('Min. 1 adult'),
                            value: '',
                        },
                    ],
                },
                enable: false,
                deprecated: false,
                creditBased: true,
            },
            {
                id: PackageCategory.FerienPassB,
                title: t('Easy Ferien-Spass'),
                description: (
                    <div>
                        <h3>
                            <b>
                                {' '}
                                {t('You drop off – we take over: Our active childcare during _season holidays', {
                                    season: t('Autumn'),
                                })}
                            </b>
                        </h3>
                        <ul className={clsx('list-disc pl-5')}>
                            <li>
                                <b>{t('Monday till Friday, at 14`o clock')}</b>
                            </li>
                            <li
                                dangerouslySetInnerHTML={{
                                    __html: t('% discount for all children under 18 years', { percent: 25 }),
                                }}
                            ></li>
                            <li
                                dangerouslySetInnerHTML={{
                                    __html: t('For childcare you pay only 5 CHF per child'),
                                }}
                            ></li>
                            <li>{t("Children's groups can be registered in advance by phone")}</li>
                            <li
                                dangerouslySetInnerHTML={{
                                    __html: t('Available until {validUntil}', {
                                        validUntil: validUntil,
                                    }),
                                }}
                            ></li>
                        </ul>
                    </div>
                ),
                timeAvailableText: t('2, 4 or 6 hours'),
                images: [
                    '/assets/images/package_category/Ferien/Easy/Easy_Bild1.jpg',
                    '/assets/images/package_category/Ferien/Easy/Easy_Bild2.jpg',
                    '/assets/images/package_category/Ferien/Easy/Easy_Bild3.jpg',
                ],
                color: 'red-400',
                price: 34.25,
                shortTitle: t('Special offer for the _SEASON', {
                    season: t('Autumn'),
                }),
                tags: [
                    { iconName: 'calendar', title: t('Monday till Friday') },
                    {
                        iconName: 'persons',
                        title: t('personsminimum', { count: 1 }),
                    },
                    { iconName: 'activities', title: t('20+ Attractions') },
                ],
                buttonText: t('Book now'),
                showMoreDetail: true,
                priceImages: [
                    {
                        image: '/assets/images/package_category/details/gelb.webp',
                        alt: '',
                    },
                    {
                        image: '/assets/images/package_category/details/rot.webp',
                        alt: '',
                    },
                ],
                extra: {
                    minDaysFromNow: 0,
                    maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
                    releaseDate: new Date('2025-09-29'),
                    maximumDate: new Date('2025-10-17'),
                },
                categoryInfo: {
                    upsell_percent: 0,
                    title: t('Group offer'),
                    subtitle: t(
                        "ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "
                    ),
                    infoText: [
                        t(
                            'Additional kart rides can be purchased for 15 CHF at the kart track for both types of tickets, if capacity is available.'
                        ),
                        t('Balaclavas (for karting) -> CHF 2.00 each (not applicable if you bring your own balaclava)'),
                        t('Please note that cash payments are not possible on site. Attractions cannot be reserved.'),
                    ],
                    infoValues: [
                        {
                            key: t('All children aged 8 to 17 years get % discount', { percent: 25 }),
                            value: '',
                        },
                        {
                            key: t('The Ferienpass is available in two versions: red and yellow'),
                            value: '',
                        },
                        {
                            key: t('With and without karting possible in the same reservation'),
                            value: '',
                        },
                        {
                            key: t('The ticket is valid Monday till Friday'),
                            value: '',
                        },
                        {
                            key: t('Children are accompanied by adult person from Arbo Park personal'),
                            value: '',
                        },
                    ],
                },
                enable: false,
                deprecated: false,
                creditBased: true,
            },
            {
                id: PackageCategory.ThreeTwo,
                title: t('3-for-2 Ferien-spass'),
                description: (
                    <div>
                        <h3>
                            <b>Für alle, die in den Sommerferien nicht genug bekommen können:</b>
                        </h3>

                        <ul className={clsx('list-disc pl-5')}>
                            <li className={clsx('text-arbo-red font-bold')}>
                                Gilt für 1 Person und 3 separate Besuche
                            </li>
                            <li>
                                Beim Kauf von 3 Tickets, <b>1 x Gratis-Eintritt erhalten</b>
                            </li>
                            <li>
                                Gilt <b>Montag bis Freitag</b>
                            </li>
                            <li>
                                <b>2, 4 oder 6 Stunden Tickets</b>, mit oder ohne Karting
                            </li>
                            <li>
                                Nur <b>bis {validUntil}</b> erhältlich
                            </li>
                        </ul>
                    </div>
                ),
                timeAvailableText: t('2, 4 or 6 hours'),
                images: [
                    '/assets/images/package_category/Ferien/3for2/3for2_Bild1.webp',
                    '/assets/images/package_category/Ferien/3for2/3for2_Bild2.webp',
                    '/assets/images/package_category/Ferien/3for2/3for2_Bild3.webp',
                ],
                color: 'green-400',
                price: 78,
                shortTitle: t('Special offer for the _SEASON', {
                    season: t('Autumn'),
                }),
                tags: [
                    { iconName: 'calendar', title: t('Monday till Friday') },
                    {
                        iconName: 'persons',
                        title: t('personsminimum', { count: 1 }),
                    },
                    { iconName: 'activities', title: t('20+ Attractions') },
                ],
                buttonText: t('Buy now'),
                showMoreDetail: false,
                enable: false,
                categoryInfo: null,
                deprecated: true,
                creditBased: true,
            },
        ],
        [t]
    )

    const eventsSubcategories: PackageCategoryEntity[] = useMemo(
        () => [
            {
                id: PackageCategory.EventMegaFun,
                title: t('Mega Fun Package'),
                description: (
                    <div>
                        <b>{t('Your ultimate adventure awaits!')}</b> {t('Everything you need for your perfect day!')}
                        <br />
                        <br />
                        <ul className={clsx('list-disc pl-5')}>
                            <li>
                                <b>{t('Suitable for 4 people')}</b> {t('or more')}
                            </li>
                            <li>
                                <b>{t('3, 4.5 or 6 hour tickets')}</b>, {t('with or without karting')}
                            </li>
                            <li>
                                <b>{t('Quick Access')}</b>
                            </li>
                            <li>
                                <b>{t('Reserved area')}</b> {t('in the restaurant area')}
                            </li>
                            <li>
                                <b>{t('Welcome host')}</b>
                            </li>
                            <li>
                                <b>{t('A printout of your group picture')}</b>
                            </li>
                            <li>
                                <span>{t('For tickets with karting:')}</span>
                                <b> {t('free balaclava and priority access with karting')}</b>
                            </li>
                        </ul>
                        <br />
                        <div>
                            <span>{t('Whether with friends, family, or colleagues – ')}</span>
                            <span>
                                <b>{t('the Mega Fun Package makes your visit to Arbopark unforgettable.')} </b>
                            </span>
                            <span>
                                {t('With Quick Access, an exclusive zone in the dining area, a personal')}
                                &nbsp;
                                <b>{t('welcome host')}</b>, {t('and a guaranteed karting race, you’re all set.')}
                            </span>
                            <br />
                            <span>
                                Plus: <b>{t('Your group photo is ready to take home with you!')}</b>
                            </span>
                        </div>
                    </div>
                ),
                timeAvailableText: t('3, 4.5 or 6 hours'),
                images: [
                    '/assets/images/package_category/MegaFun/Mega_Paket_2.webp',
                    '/assets/images/package_category/MegaFun/Mega_Paket_1.webp',
                    '/assets/images/package_category/MegaFun/Mega_Paket_3.webp',
                ],
                color: 'yellow-400',
                price: 63,
                shortTitle: t('Possible to reserve with and without karting in the same reservation'),
                tags: [
                    { iconName: 'calendar', title: t('Wednesday till Sunday') },
                    {
                        iconName: 'persons',
                        title: t('personsminimum', { count: 4 }),
                    },
                    { iconName: 'activities', title: t('20+ Attractions') },
                ],
                buttonText: t('Book now'),
                showMoreDetail: true,
                priceImages: [
                    {
                        image: '/assets/images/package_category/MegaFun/Mega_Paket_Prices.webp',
                        alt: '',
                    },
                ],
                extra: {
                    minDaysFromNow: 3,
                    maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
                    expiryDate: new Date('2026-08-31'),
                },
                categoryInfo: {
                    upsell_percent: 0,
                    title: t('Group offer'),
                    subtitle: t(
                        "ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "
                    ),
                    infoText: [
                        t(
                            'Additional kart rides can be purchased at the kart track with both ticket types, subject to availability, for 15 CHF.'
                        ),
                        t('Please note that cash payment is not possible on-site, and attractions cannot be reserved.'),
                    ],
                    infoValues: [],
                    infoEventPackage: {
                        title: t('In PACKAGE package included', {
                            packageName: 'Mega Fun',
                        }),
                        values: [
                            'Quick Access',
                            'Reservierte Zone im Gastro Bereich',
                            'Welcome GameHost',
                            'Ausgedrucktes Gruppenfoto',

                            'Kopfhaube (mit Karting Ticket)',
                            'Priority Access beim Karting (mit Karting Ticket)',
                        ],
                    },
                },
                enable: true,
                deprecated: false,
                creditBased: false,
            },
            {
                id: PackageCategory.EventNonPlusUltra,
                title: t('Non Plus Ultra Package'),
                description: (
                    <div>
                        <b>{t('The best experience')}</b>&nbsp;
                        <span>{t('for groups who want to enjoy a true VIP feeling at Arbopark:')}</span>
                        <br />
                        <br />
                        <ul className={clsx('list-disc pl-5')}>
                            <li>
                                <b>{t('Suitable for 8 people')}</b> {t('or more')}
                            </li>
                            <li>
                                <b>{t('3, 4.5 or 6 hour tickets')}</b>, {t('with or without karting')}
                            </li>
                            <li>
                                <b>{t('Priority Check-in')}</b>
                            </li>
                            <li>
                                <b>{t('Welcome Drink')}</b>&nbsp;
                                <span>{t('(Non-alcoholic)')}</span>
                            </li>
                            <li>
                                <b>{t('Reserved area')}</b> {t('in the restaurant area')}
                            </li>
                            <li>
                                <b>{t('VIP GameHost')}</b>
                            </li>
                            <li>
                                <b>{t('Unlimited access ')}</b>
                                <span>{t('Cube Challenges')}</span>
                            </li>
                            <li>
                                <b>{t('Exclusive')}</b> <span>{t('timeslots')}</span>
                            </li>
                            <li>
                                <b>{t('A printout of your group picture')}</b>
                            </li>
                            <li>
                                <span>{t('For tickets with karting:')}</span>
                                <b> {t('free balaclava and priority access')}</b>
                                ,&nbsp;
                                <span>{t('Competition Modus')}</span>
                            </li>
                        </ul>
                        <br />
                        <div>
                            <span>{t('Your visit here will be an')}</span>{' '}
                            <b>
                                <span>{t('absolute highlight!')}</span>&nbsp;
                            </b>
                            <span>
                                {t(
                                    'Enjoy priority check-in, refreshing welcome drinks, and an exclusive dining area just for you. '
                                )}
                            </span>
                            <span>
                                {t(
                                    'With your VIP game host, unlimited access to the Cube Challenges, and special race times, '
                                )}
                            </span>
                            <b>
                                <span>{t("you'll experience Arbopark with no compromises")}</span>
                            </b>
                            <span>&nbsp;{t('– including Competion Mode!')}</span>
                        </div>
                    </div>
                ),
                timeAvailableText: t('3, 4.5 or 6 hours'),
                images: [
                    '/assets/images/package_category/NonPlusUltra/NonPlusUltra_Paket_1.webp',
                    '/assets/images/package_category/NonPlusUltra/NonPlusUltra_Paket_2.webp',
                    '/assets/images/package_category/NonPlusUltra/NonPlusUltra_Paket_3.webp',
                ],
                color: 'yellow-400',
                price: 70,
                shortTitle: t('Possible to reserve with and without karting in the same reservation'),
                tags: [
                    { iconName: 'calendar', title: t('Wednesday till Sunday') },
                    {
                        iconName: 'persons',
                        title: t('personsminimum', { count: 8 }),
                    },
                    { iconName: 'activities', title: t('20+ Attractions') },
                ],

                buttonText: t('Book now'),
                showMoreDetail: true,
                priceImages: [
                    {
                        image: '/assets/images/package_category/NonPlusUltra/NonPlusUltra_prices.webp',
                        alt: '',
                    },
                ],
                extra: {
                    minDaysFromNow: 3,
                    maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
                    expiryDate: new Date('2026-08-31'),
                },
                categoryInfo: {
                    upsell_percent: 0,
                    title: t('Group offer'),
                    subtitle: t(
                        "ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "
                    ),
                    infoText: [
                        t(
                            'Additional kart rides can be purchased at the kart track with both ticket types, subject to availability, for 15 CHF.'
                        ),
                        t('Please note that cash payment is not possible on-site, and attractions cannot be reserved.'),
                    ],
                    infoValues: [],
                    infoEventPackage: {
                        title: t('In PACKAGE package included', {
                            packageName: 'Ultra',
                        }),
                        values: [
                            'Priority Check-in',
                            'Welcome Drink (Non-alcoholic)',
                            'Reservierte Zone im Gastro Bereich',
                            'VIP GameHost',
                            'Ausgedrucktes Gruppenfoto',

                            'Kopfhaube (mit Karting Ticket)',
                            'Priority Access beim Karting (mit Karting Ticket)',
                            'Exklusive Stint-Garantie (mit Karting Ticket)',
                            'Competition Modus (mit Karting Ticket)',

                            'Unlimitierter Access Cube Challenges (ohne Karting)',
                            'Exklusive Timeslots (ohne Karting)',
                        ],
                    },
                },
                enable: true,
                deprecated: false,
                creditBased: false,
            },
            {
                id: PackageCategory.PolterabendMegaFun,
                title: t('Polterabend Mega Fun Package'),
                description: (
                    <div>
                        <b>{t('Your bachelor/bachelorette party will be amazing!')}</b>{' '}
                        {t('Everything you need for an unforgettable day with your crew:')}
                        <br />
                        <br />
                        <ul className={clsx('list-disc pl-5')}>
                            <li>
                                <b>{t('Suitable for 4 people')}</b> {t('or more')}
                            </li>
                            <li>
                                <b>{t('3, 4.5 or 6 hour tickets')}</b>, {t('with or without karting')}
                            </li>
                            <li>
                                <b>{t('Quick Access')}</b>
                            </li>
                            <li>
                                <b>{t('Reserved area')}</b> {t('in the restaurant area')}
                            </li>
                            <li>
                                <b>{t('Welcome host')}</b>
                            </li>
                            <li>
                                <b>{t('A printout of your group picture')}</b>
                            </li>
                            <li>
                                <span>{t('For tickets with karting:')}</span>
                                &nbsp;
                                <span>{t('free balaclava and priority access with karting')}</span>
                            </li>
                            <li>{t('There are special bachelor party add-ons available to book!')}</li>
                        </ul>
                        <br />
                        <div>
                            <span>{t('Party with your friends and enjoy endless fun – ')}</span>
                            <span>
                                <b>
                                    {t(
                                        'the Mega Fun Package makes your bachelor/bachelorette party at Arbopark legendary!'
                                    )}
                                </b>
                            </span>
                            &nbsp;
                            <span>
                                {t(
                                    'Quick access, a reserved zone in the dining area, a personal welcome host, and a guaranteed karting race ensure pure action.'
                                )}
                                &nbsp;
                            </span>
                            <span>
                                <b>{t('And as the highlight, you’ll get your group photo to take home right away!')}</b>
                            </span>
                        </div>
                    </div>
                ),
                timeAvailableText: t('3, 4.5 or 6 hours'),
                images: [
                    '/assets/images/package_category/PolterabendMegaFun/PolterabendMegaFun_Paket_1.webp',
                    '/assets/images/package_category/PolterabendMegaFun/PolterabendMegaFun_Paket_2.webp',
                    '/assets/images/package_category/PolterabendMegaFun/PolterabendMegaFun_Paket_3.webp',
                ],
                color: 'yellow-400',
                price: 63,
                shortTitle: t('Possible to reserve with and without karting in the same reservation'),
                tags: [
                    { iconName: 'calendar', title: t('Wednesday till Sunday') },
                    {
                        iconName: 'persons',
                        title: t('personsminimum', { count: 4 }),
                    },
                    { iconName: 'activities', title: t('20+ Attractions') },
                ],
                buttonText: t('Book now'),
                showMoreDetail: true,
                priceImages: [
                    {
                        image: '/assets/images/package_category/PolterabendMegaFun/PolterabendMegaFun_Prices.webp',
                        alt: '',
                    },
                ],
                extra: {
                    minDaysFromNow: 3,
                    maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
                    expiryDate: new Date('2026-08-31'),
                },
                categoryInfo: {
                    upsell_percent: 0,
                    title: t('Group offer'),
                    subtitle: t(
                        "ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "
                    ),
                    infoText: [
                        t(
                            'Additional kart rides can be purchased at the kart track with both ticket types, subject to availability, for 15 CHF.'
                        ),
                        t('Please note that cash payment is not possible on-site, and attractions cannot be reserved.'),
                    ],
                    infoValues: [],
                    infoEventPackage: {
                        title: t('In PACKAGE package included', {
                            packageName: 'Mega Fun',
                        }),
                        values: [
                            'Quick Access',
                            'Reservierte Zone im Gastro Bereich',
                            'Welcome GameHost',
                            'Ausgedrucktes Gruppenfoto',

                            'Kopfhaube (mit Karting Ticket)',
                            'Priority Access beim Karting (mit Karting Ticket)',
                        ],
                    },
                },
                enable: true,
                deprecated: false,
                creditBased: false,
            },
            {
                id: PackageCategory.PolterabendNonPlusUltra,
                title: t('Polterabend Non Plus Ultra Package'),
                description: (
                    <div>
                        <b>{t('The ultimate VIP experience for a bachelor/bachelorette party ')}</b>
                        {t('that’s meant to top them all:')}
                        <br />
                        <br />
                        <ul className={clsx('list-disc pl-5')}>
                            <li>
                                <b>{t('Suitable for 8 people')}</b> {t('or more')}
                            </li>
                            <li>
                                <b>{t('3, 4.5 or 6 hour tickets')}</b>, {t('with or without karting')}
                            </li>
                            <li>
                                <b>{t('Priority Check-in')}</b>
                            </li>
                            <li>
                                <b>{t('Welcome Drink')}</b>&nbsp;
                                <span>{t('(Non-alcoholic)')}</span> {t('or an alcoholic')} <b>Welcome Shot</b>
                            </li>
                            <li>
                                <b>{t('Reserved area')}</b> {t('in the restaurant area')}
                            </li>
                            <li>
                                <b>{t('VIP GameHost')}</b>
                            </li>
                            <li>
                                <b>{t('Unlimited access ')}</b>
                                <span>{t('Cube Challenges')}</span>
                            </li>
                            <li>
                                <b>{t('Exclusive')}</b> <span>{t('timeslots')}</span>
                            </li>
                            <li>
                                <b>{t('A printout of your group picture')}</b>
                            </li>
                            <li>
                                <span>{t('For tickets with karting:')}</span>
                                <b> {t('free balaclava and priority access')}</b>
                                ,&nbsp;
                                <span>{t('Competition Modus')}</span>
                            </li>
                            <li>{t('There are special bachelor party add-ons available to book!')}</li>
                        </ul>
                        <br />
                        <div>
                            <b>
                                <span>{t('This is where the party of your life takes place! ')}</span>
                            </b>
                            <span>
                                {t(
                                    'Enjoy priority check-in, refreshing welcome drinks, and an exclusive dining area just for you. '
                                )}
                            </span>
                            <span>
                                {t(
                                    'With a VIP game host, unlimited access to the Cube Challenges, and your own race times, this evening will be simply unbeatable – including Competion Mode.'
                                )}
                                &nbsp;
                            </span>
                            <b>
                                <span>{t('Perfect for celebrating the big day in style!')}</span>
                            </b>
                        </div>
                    </div>
                ),
                timeAvailableText: t('3, 4.5 or 6 hours'),
                images: [
                    '/assets/images/package_category/PolterabendNonPlusUltra/PolterabendNonPlusUltra_Paket_1.webp',
                    '/assets/images/package_category/PolterabendNonPlusUltra/PolterabendNonPlusUltra_Paket_2.webp',
                    '/assets/images/package_category/PolterabendNonPlusUltra/PolterabendNonPlusUltra_Paket_3.webp',
                ],
                color: 'yellow-400',
                price: 70,
                shortTitle: t('Possible to reserve with and without karting in the same reservation'),
                tags: [
                    { iconName: 'calendar', title: t('Wednesday till Sunday') },
                    {
                        iconName: 'persons',
                        title: t('personsminimum', { count: 8 }),
                    },
                    { iconName: 'activities', title: t('20+ Attractions') },
                ],
                buttonText: t('Book now'),
                showMoreDetail: true,
                priceImages: [
                    {
                        image: '/assets/images/package_category/PolterabendNonPlusUltra/PolterabendNonPlusUltra_Prices.webp',
                        alt: '',
                    },
                ],
                extra: {
                    minDaysFromNow: 3,
                    maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
                    expiryDate: new Date('2026-08-31'),
                },
                categoryInfo: {
                    upsell_percent: 0,
                    title: t('Group offer'),
                    subtitle: t(
                        "ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "
                    ),
                    infoText: [
                        t(
                            'Additional kart rides can be purchased at the kart track with both ticket types, subject to availability, for 15 CHF.'
                        ),
                        t('Please note that cash payment is not possible on-site, and attractions cannot be reserved.'),
                    ],
                    infoValues: [],
                    infoEventPackage: {
                        title: t('In PACKAGE package included', {
                            packageName: 'Ultra',
                        }),
                        values: [
                            'Priority Check-in',
                            'Welcome Drink (Non-alcoholic)',
                            'Reservierte Zone im Gastro Bereich',
                            'VIP GameHost',
                            'Ausgedrucktes Gruppenfoto',

                            'Kopfhaube (mit Karting Ticket)',
                            'Priority Access beim Karting (mit Karting Ticket)',
                            'Exklusive Stint-Garantie (mit Karting Ticket)',
                            'Competition Modus (mit Karting Ticket)',

                            'Unlimitierter Access Cube Challenges (ohne Karting)',
                            'Exklusive Timeslots (ohne Karting)',
                        ],
                    },
                },
                enable: true,
                deprecated: false,
                creditBased: false,
            },
            {
                id: PackageCategory.Kinder,
                title: t('Children Birthday'),
                description: (
                    <div>
                        <b>{t('Happy Birthday!')}</b>{' '}
                        {t('Celebrate your birthday with friends and family at ArboPark!')}
                        <br /> <br />
                        <ul className={clsx('list-disc pl-5')}>
                            <li>
                                <b>{t('Bookable for 5 people')}</b> {t('or more')}
                            </li>
                            <li>
                                <b>{t('4 or 6 hour tickets')}</b>, {t('with or without karting')}
                            </li>
                            <li>
                                {t('Children under 16 years of age receive')}
                                &nbsp;
                                <b>{t('25% discount on the ticket price')}</b>
                            </li>
                            <li>
                                {t('the birthday child receives')} <b>{t('a small surprise')}</b>
                            </li>
                            <li>
                                <b>{t('Reserved area')}</b> {t('in the catering area')}
                            </li>
                            <li>
                                {t('For tickets with karting')}: <b>{t('free helmet')}</b> {t('at the kart track')}
                            </li>
                            <li>{t('Special birthday add-ons can be booked!')}</li>
                        </ul>
                        <br />
                        <span>
                            {t(
                                'Whether a celebration in a smaller circle or a larger party - ArboPark is the ideal place to celebrate your special day!'
                            )}
                        </span>
                    </div>
                ),
                timeAvailableText: t('4 or 6 hours'),
                images: [
                    '/assets/images/package_category/Kinder/KG_1.webp',
                    '/assets/images/package_category/Kinder/KG_2.webp',
                    '/assets/images/package_category/Kinder/KG_3.webp',
                ],
                color: 'red-400',
                price: 41.25,
                shortTitle: t('Possible to reserve with and without karting in the same reservation'),
                tags: [
                    { iconName: 'calendar', title: t('Wednesday till Sunday') },
                    {
                        iconName: 'persons',
                        title: t('personsminimum', { count: 5 }),
                    },
                    { iconName: 'activities', title: t('20+ Attractions') },
                ],
                buttonText: t('Book now'),
                showMoreDetail: true,
                priceImages: [
                    {
                        image: '/assets/images/package_category/Fun/gelb.webp',
                        alt: '',
                    },
                    {
                        image: '/assets/images/package_category/Fun/rot.webp',
                        alt: '',
                    },
                ],
                extra: {
                    minDaysFromNow: 3,
                    maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
                    expiryDate: new Date('2026-08-31'),
                },
                categoryInfo: {
                    upsell_percent: 0,
                    title: t('Group offer'),
                    subtitle: t(
                        "ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "
                    ),
                    infoText: [
                        t(
                            'Discounts on extras apply only to online bookings and not to additional purchases during the visit to Arbopark.'
                        ),
                        t(
                            'Additional kart rides can be purchased at the kart track with both ticket types, subject to availability, for 15 CHF.'
                        ),
                        t('Please note that cash payment is not possible on-site, and attractions cannot be reserved.'),
                    ],
                    infoValues: [],
                    infoEventPackage: {
                        title: t('In PACKAGE package included', {
                            packageName: 'Kinder ',
                        }),
                        values: [t('Balaclavas (with karting ticket)')],
                    },
                },
                enable: true,
                deprecated: false,
                creditBased: false,
            },
            {
                id: PackageCategory.School,
                title: t('School-Club Package'),
                description: (
                    <div>
                        <ul className={clsx('list-disc pl-5')}>
                            <li>
                                <b>{t('2, 4 or 6 hours tickets')}</b>, {t('with or without karting')}
                            </li>
                            <li>
                                {t(
                                    'The accompanying person receives free admission (one additional free accompanying person per 25 children).'
                                )}
                            </li>

                            <li>
                                {t('Children under 18 years of age receive')}
                                &nbsp;
                                <b>{t('25% discount on the ticket price')}</b>
                            </li>
                            <li>
                                {t(
                                    'On special request (events@arbopark.ch), larger groups can also be received outside opening hours.'
                                )}
                            </li>
                        </ul>
                        <br />
                        <span>{t('Schools and associations can book this package ')}</span>
                        <b>{t('every day that ArboPark is open')}</b>.{' '}
                        {t("It's the perfect package for groups to experience fun and action together!")}
                    </div>
                ),
                timeAvailableText: t('2, 4 or 6 hours'),
                images: [
                    '/assets/images/package_category/standart_1.webp',
                    '/assets/images/package_category/standart_2.webp',
                    '/assets/images/package_category/standart_3.webp',
                ],
                color: 'red-400',
                price: 29.25,
                shortTitle: t('The ideal offer for schools and associations!'),
                tags: [
                    { iconName: 'calendar', title: t('Every day') },
                    {
                        iconName: 'persons',
                        title: t('personsminimum', { count: 10 }),
                    },
                    { iconName: 'activities', title: t('20+ Attractions') },
                ],
                buttonText: t('Book now'),
                showMoreDetail: true,
                priceImages: [
                    {
                        image: '/assets/images/package_category/details/gelb.webp',
                        alt: '',
                    },
                    {
                        image: '/assets/images/package_category/details/rot.webp',
                        alt: '',
                    },
                ],
                extra: {
                    minDaysFromNow: 1,
                    maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
                },
                categoryInfo: {
                    upsell_percent: 0,
                    title: t('Group offer'),
                    subtitle: t(
                        "ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "
                    ),
                    infoText: [
                        t(
                            'Additional kart rides can be purchased for 15 CHF at the kart track for both types of tickets, if capacity is available.'
                        ),
                        t('Balaclavas (for karting) -> CHF 2.00 each (not applicable if you bring your own balaclava)'),
                        t('Please note that cash payments are not possible on site. Attractions cannot be reserved.'),
                    ],
                    infoValues: [
                        {
                            key: t('All children aged 8 to 15 years get 25% discount'),
                            value: '',
                        },
                        {
                            key: t('The School / Club Ticket is available in two versions: red and yellow'),
                            value: '',
                        },
                        {
                            key: t('With and without karting possible in the same reservation'),
                            value: '',
                        },
                        {
                            key: t('The ticket is valid every day'),
                            value: '',
                        },
                        {
                            key: t('Min. 1 adult (at regular rate)'),
                            value: '',
                        },
                    ],
                },
                enable: false,
                deprecated: false,
                creditBased: false,
            },
            {
                id: PackageCategory.SchoolTrophy,
                title: t('School-Trophy'),
                description: (
                    <div>
                        <ul className={clsx('list-disc pl-5')}>
                            <li>
                                <b>{t('2, 3 or 4.5 hours tickets')}</b>, {t('with or without karting')}
                            </li>
                            <li>
                                {t(
                                    'The accompanying person receives free admission (one additional free accompanying person per 25 children).'
                                )}
                            </li>

                            <li>
                                {t('Children under 18 years of age receive')}
                                &nbsp;
                                <b>{t('25% discount on the ticket price')}</b>
                            </li>
                            <li>
                                {t(
                                    'On special request (events@arbopark.ch), larger groups can also be received outside opening hours.'
                                )}
                            </li>
                        </ul>
                        <br />
                        <span>{t('Schools and associations can book this package ')}</span>
                        <b>{t('every day that ArboPark is open')}</b>.{' '}
                        {t("It's the perfect package for groups to experience fun and action together!")}
                    </div>
                ),
                timeAvailableText: t('2, 3 or 4.5 hours'),
                images: [
                    '/assets/images/package_category/standart_1.webp',
                    '/assets/images/package_category/standart_2.webp',
                    '/assets/images/package_category/standart_3.webp',
                ],
                color: 'red-400',
                price: 25.25,
                shortTitle: t('The ideal offer for schools and associations!'),
                tags: [
                    { iconName: 'calendar', title: t('Monday till Friday') },
                    {
                        iconName: 'persons',
                        title: t('personsminimum', { count: 15 }),
                    },
                    { iconName: 'activities', title: t('20+ Attractions') },
                ],
                buttonText: t('Book now'),
                showMoreDetail: true,
                priceImages: [
                    {
                        image: '/assets/images/package_category/details/gelb.webp',
                        alt: '',
                    },
                    {
                        image: '/assets/images/package_category/details/rot.webp',
                        alt: '',
                    },
                ],
                extra: {
                    minDaysFromNow: 1,
                    maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
                },
                categoryInfo: {
                    upsell_percent: 0,
                    title: t('Group offer'),
                    subtitle: t(
                        "ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "
                    ),
                    infoText: [
                        t(
                            'Additional kart rides can be purchased for 10 CHF at the kart track for both types of tickets, if capacity is available.'
                        ),
                        t('Balaclavas (for karting) -> CHF 2.00 each (not applicable if you bring your own balaclava)'),
                        t('Please note that cash payments are not possible on site. Attractions cannot be reserved.'),
                    ],
                    infoValues: [
                        {
                            key: t('All children aged 8 to 15 years get 25% discount'),
                            value: '',
                        },
                        {
                            key: t('With and without karting possible in the same reservation'),
                            value: '',
                        },
                        {
                            key: t('The ticket is valid every day'),
                            value: '',
                        },
                        {
                            key: t('Min. 1 adult (at regular rate)'),
                            value: '',
                        },
                    ],
                },
                enable: true,
                deprecated: false,
                creditBased: false,
            },
            {
                id: PackageCategory.MegaFunSchool,
                title: t('Mega Fun School Package'),
                description: (
                    <div>
                        <ul className={'list-disc pl-5'}>
                            <li>
                                <b>{t('2, 3 or 4.5 hours tickets')}</b>, {t('with or without karting')}
                            </li>
                            <li>{t('Children until 17.9 years old')}</li>
                            <li
                                dangerouslySetInnerHTML={{
                                    __html: t(
                                        'Adults pay the same price as children, with a maximum of 25% adult share.'
                                    ),
                                }}
                            ></li>
                            <li>{t('Extra karting for only CHF 10.-')}</li>
                            <li>
                                <b>{t('Quick Access')}</b>
                            </li>
                            {/*<li>*/}
                            {/*    <b>{t("Category A Competition")}</b> {t("on tickets with 3 and 4.5 hours")}*/}
                            {/*</li>*/}
                            <li>
                                <b>{t('1 Shorly per Person gratis')}</b> {t('in the restaurant area')}
                            </li>

                            <li>
                                <b>{t('Welcome host')}</b>
                            </li>
                            <li>
                                <b>{t('A printout of your group picture')}</b>
                            </li>
                            <li>
                                {t(
                                    'On special request (events@arbopark.ch), larger groups can also be received outside opening hours.'
                                )}
                            </li>
                            <li>
                                {t('For tickets with karting')}: <b>{t('free helmet')}</b> {t('at the kart track')}
                            </li>
                        </ul>
                        <br />
                        <span>{t('Schools and associations can book this package ')}</span>
                        <b>{t('book Monday till Friday')}</b>.{' '}
                        {t("It's the perfect package for groups to experience fun and action together!")}
                    </div>
                ),
                timeAvailableText: t('2, 3 or 4.5 hours'),
                images: [
                    '/img/package_category/standart_1.webp',
                    '/img/package_category/standart_2.webp',
                    '/img/package_category/standart_3.webp',
                ],
                color: 'red-400',
                price: 29.75,
                shortTitle: t('The ideal offer for schools and associations!'),
                tags: [
                    { iconName: 'calendar', title: t('Monday till Friday') },
                    { iconName: 'persons', title: t('personsminimum', { count: 10 }) },
                    { iconName: 'activities', title: t('20+ Attractions') },
                ],
                buttonText: t('Book now'),
                showMoreDetail: true,
                priceImages: [
                    {
                        image: '/img/package_category/details/gelb.webp',
                        alt: '',
                    },
                    {
                        image: '/img/package_category/details/rot.webp',
                        alt: '',
                    },
                ],
                extra: {
                    minDaysFromNow: 1,
                    maxDaysFromNow: MAX_MONTH_COUNT_FOR_AVAILABLE_DATES * 30,
                },
                categoryInfo: {
                    upsell_percent: 0,
                    title: t('Group offer'),
                    subtitle: t(
                        "ArboPark is especially fun when you visit it with a group of people. That's why we offer you attractive group offers: "
                    ),
                    infoText: [
                        t(
                            'Additional kart rides can be purchased for PRICE CHF at the kart track for both types of tickets, if capacity is available.',
                            {
                                price: 10,
                            }
                        ),
                        t('Balaclavas (for karting) -> CHF 2.00 each (not applicable if you bring your own balaclava)'),
                        t('Please note that cash payments are not possible on site. Attractions cannot be reserved.'),
                    ],
                    infoValues: [
                        {
                            key: t('All children aged 8 to 17.9 years'),
                            value: '',
                        },
                        {
                            key: t('Maximum 25% Adults allowed'),
                            value: '',
                        },
                        {
                            key: t('The School / Club Ticket is available in two versions: red and yellow'),
                            value: '',
                        },
                        {
                            key: t('With and without karting possible in the same reservation'),
                            value: '',
                        },
                        {
                            key: t('The ticket is valid every day from Monday till Friday'),
                            value: '',
                        },
                        {
                            key: t('Min. 1 adult (at regular rate)'),
                            value: '',
                        },
                    ],
                },
                enable: true,
                deprecated: false,
                creditBased: false,
            },
            {
                id: PackageCategory.MegaFun,
                title: t('Mega Fun'),
                description: t('Mega Fun Package - Available from 01.08.2026'),
                images: [],
                color: 'red-400',
                price: 0,
                shortTitle: '',
                tags: [],
                buttonText: t('Book now'),
                timeAvailableText: t('3, 4.5 or 8 hours'),
                showMoreDetail: false,
                enable: true,
                categoryInfo: null,
                deprecated: false,
                creditBased: true,
                extra: {
                    releaseDate: '2026-08-01',
                },
            },
            {
                id: PackageCategory.GigaFun,
                title: t('Giga Fun'),
                description: t('Giga Fun Package - Available from 01.08.2026'),
                images: [],
                color: 'red-400',
                price: 0,
                shortTitle: '',
                tags: [],
                buttonText: t('Book now'),
                timeAvailableText: t('3, 4.5 or 8 hours'),
                showMoreDetail: false,
                enable: true,
                categoryInfo: null,
                deprecated: false,
                creditBased: true,
                extra: {
                    releaseDate: '2026-08-01',
                },
            },
            {
                id: PackageCategory.NonPlusUltra,
                title: t('Non Plus Ultra'),
                description: t('Non Plus Ultra Extra Fun Package - Available from 01.08.2026'),
                images: [],
                color: 'red-400',
                price: 0,
                shortTitle: '',
                tags: [],
                buttonText: t('Book now'),
                timeAvailableText: t('3, 4.5 or 8 hours'),
                showMoreDetail: false,
                enable: true,
                categoryInfo: null,
                deprecated: false,
                creditBased: true,
                extra: {
                    releaseDate: '2026-08-01',
                },
            },
            {
                id: PackageCategory.HappyKids,
                title: t('Happy Kids'),
                description: t('Happy Kids Package - Available from 01.08.2026'),
                images: [],
                color: 'red-400',
                price: 0,
                shortTitle: '',
                tags: [],
                buttonText: t('Book now'),
                timeAvailableText: t('3, 4.5 hours'),
                showMoreDetail: false,
                enable: true,
                categoryInfo: null,
                deprecated: false,
                creditBased: true,
                extra: {
                    releaseDate: '2026-08-01',
                },
            },
            {
                id: PackageCategory.HappyKidsPlus,
                title: t('Happy Kids Plus'),
                description: t('Happy Kids Plus Package - Available from 01.08.2026'),
                images: [],
                color: 'red-400',
                price: 0,
                shortTitle: '',
                tags: [],
                buttonText: t('Book now'),
                timeAvailableText: t('3, 4.5 hours'),
                showMoreDetail: false,
                enable: true,
                categoryInfo: null,
                deprecated: false,
                creditBased: true,
                extra: {
                    releaseDate: '2026-08-01',
                },
            },
            {
                id: PackageCategory.HappyParty,
                title: t('Happy Party'),
                description: t('Happy Party - Available from 01.08.2026'),
                images: [],
                color: 'red-400',
                price: 0,
                shortTitle: '',
                tags: [],
                buttonText: t('Book now'),
                timeAvailableText: t('3, 4.5 or 8 hours'),
                showMoreDetail: false,
                enable: true,
                categoryInfo: null,
                deprecated: false,
                creditBased: true,
                extra: {
                    releaseDate: '2026-08-01',
                },
            },
            {
                id: PackageCategory.HappyPartyPlus,
                title: t('Happy Party Plus'),
                description: t('Happy Party Plus Package - Available from 01.08.2026'),
                images: [],
                color: 'red-400',
                price: 0,
                shortTitle: '',
                tags: [],
                buttonText: t('Book now'),
                timeAvailableText: t('3, 4.5 or 8 hours'),
                showMoreDetail: false,
                enable: true,
                categoryInfo: null,
                deprecated: false,
                creditBased: true,
                extra: {
                    releaseDate: '2026-08-01',
                },
            },
            {
                id: PackageCategory.HappyPartyVIP,
                title: t('Happy Party VIP'),
                description: t('Happy Party VIP Package - Available from 01.08.2026'),
                images: [],
                color: 'red-400',
                price: 0,
                shortTitle: '',
                tags: [],
                buttonText: t('Book now'),
                timeAvailableText: t('3, 4.5 or 8 hours'),
                showMoreDetail: false,
                enable: true,
                categoryInfo: null,
                deprecated: false,
                creditBased: true,
                extra: {
                    releaseDate: '2026-08-01',
                },
            },
        ],
        [t]
    )

    const packageCategoryCardHandle = (categorySlug: PackageCategory) => {
        dispatch(
            BookingAction.setFilter({
                ...bookingFilter,
                category: categorySlug,
            })
        )

        if (categorySlug === PackageCategory.Giftcard) {
            navigate('/booking/package/5')
            return
        }

        if (categorySlug === PackageCategory.Yearly) {
            navigate('/booking/package/6')
            return
        }

        if (categorySlug === PackageCategory.ThreeTwo) {
            navigate('/booking/package/21')
            return
        }

        if (categorySlug === PackageCategory.Event) {
            navigate(
                '/booking/events/subcategory' +
                    '?' +
                    appendQueryParams(bookingFilter as any)
            )
            return
        }

        if (categorySlug === PackageCategory.Ferien) {
            navigate(
                '/booking/ferien/subcategory' +
                    '?' +
                    appendQueryParams(bookingFilter as any)
            )
            return
        }

        ;(dispatch(BookingAction.initCart()) as unknown as Promise<any>).then(
            () => {
                navigate(
                    '/booking/options' +
                        '?' +
                        appendQueryParams(bookingFilter as any)
                )
            }
        )
    }

    const getCategoryBySlug = (
        slug: PackageCategory
    ): PackageCategoryEntity => {
        const fallbackCategory = categories[0]

        // Utility function to find category in a given list
        const findCategory = (
            list: PackageCategoryEntity[]
        ): PackageCategoryEntity | null => {
            const index = list.findIndex((cat) => cat.id === slug)
            return index !== -1 ? list[index] : null
        }

        // Check each category list in order of priority
        return (
            findCategory(categories) ||
            findCategory(eventsSubcategories) ||
            findCategory(ferienSubcategories) ||
            fallbackCategory
        )
    }

    /**
     * Determines the drivers count text status for a given package category and person type.
     *
     * @param {PackageCategory} category - The package category to evaluate.
     * @param {PersonType} personType - The type of person (e.g., Adult, Child, etc.) used for evaluation.
     * @returns {boolean} - Returns `false` if the person type is `Adult`.
     *                      Returns `true` if the package category is `PackageCategory.FerienPassB` and
     *                      the person type is not `Adult`. Otherwise, returns `false`.
     */
    const getPackageCategoryDriversCountText = (
        category: PackageCategory,
        personType: PersonType
    ) => {
        if (personType == PersonType.Adult) {
            return false
        }
        return category === PackageCategory.FerienPassB
    }

	const sellerCategories = [...sellerStandardCategories, ...eventsSubcategories]
    const staticEventsForDisplay = sellerCategories.filter((eventCategory) => !eventCategory.deprecated)
	const filterCategory = useTypedSelector(state => state.bookingState.bookingProcess.filter.category);
	const selectedCategoryEntity = staticEventsForDisplay.find(cat => cat.id == filterCategory)

    return {
        categories,
        sellerStandardCategories,
        staticEventsForDisplay,
        selectedCategoryEntity,
        eventsSubcategories,
        ferienSubcategories,
        getCategoryBySlug,
        getPackageCategoryDriversCountText,
        packageCategoryCardHandle,
    }
}
